import React from 'react';
import {
    AlertCircle,
    CheckCircle,
    AlertTriangle,
    Info,
    Wrench,
    TrendingUp,
    ChevronRight
} from 'lucide-react';

interface DiagnosticIssue {
    type: string;
    severity: 'error' | 'warning' | 'info';
    description: string;
    guidance: string[];
    auto_fixable: boolean;
    affected_channels?: string[];
}

interface ActionableRecommendation {
    priority: number;
    category: string;
    description: string;
    rationale: string;
    expected_impact: 'high' | 'medium' | 'low';
}

interface DiagnosticAssistantPanelProps {
    failureMode?: string;
    issues: DiagnosticIssue[];
    recommendations: ActionableRecommendation[];
    qualityScore?: number;
    onApplyFix?: (fixAction: string, params?: any) => void;
    isPreTraining?: boolean;
}

export const DiagnosticAssistantPanel: React.FC<DiagnosticAssistantPanelProps> = ({
    failureMode,
    issues,
    recommendations,
    qualityScore = 0,
    onApplyFix,
    isPreTraining = false
}) => {
    if (issues.length === 0 && !failureMode) {
        return null;
    }

    const getSeverityIcon = (severity: string) => {
        switch (severity) {
            case 'error':
                return <AlertCircle className="w-5 h-5 text-red-600" />;
            case 'warning':
                return <AlertTriangle className="w-5 h-5 text-amber-600" />;
            default:
                return <Info className="w-5 h-5 text-blue-600" />;
        }
    };

    const getSeverityBg = (severity: string) => {
        switch (severity) {
            case 'error':
                return 'bg-red-50 border-red-200';
            case 'warning':
                return 'bg-amber-50 border-amber-200';
            default:
                return 'bg-blue-50 border-blue-200';
        }
    };

    const getPriorityBadge = (priority: number) => {
        const colors = {
            1: 'bg-red-100 text-red-800',
            2: 'bg-orange-100 text-orange-800',
            3: 'bg-yellow-100 text-yellow-800',
            4: 'bg-blue-100 text-blue-800',
            5: 'bg-gray-100 text-gray-800'
        };
        return colors[priority as keyof typeof colors] || colors[5];
    };

    const getImpactBadge = (impact: string) => {
        const colors = {
            high: 'bg-green-100 text-green-800',
            medium: 'bg-blue-100 text-blue-800',
            low: 'bg-gray-100 text-gray-800'
        };
        return colors[impact as keyof typeof colors] || colors.low;
    };

    const getFixActionFromIssue = (issue: DiagnosticIssue): string | null => {
        // Map issue types to fix actions
        const mapping: Record<string, string> = {
            'too_many_channels': 'aggregate_low_spend_channels',
            'low_variability': 'remove_low_variability_channels',
            'high_correlation': 'aggregate_correlated_channels',
            'insufficient_data': 'switch_daily_to_weekly'
        };
        return mapping[issue.type] || null;
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-white">
                <div className="flex items-center gap-3">
                    <Wrench className="w-6 h-6 text-indigo-600" />
                    <div>
                        <h3 className="text-lg font-semibold text-slate-900">
                            {isPreTraining ? 'Pre-Training Diagnostics' : 'Model Diagnostics Assistant'}
                        </h3>
                        <p className="text-sm text-slate-600">
                            {isPreTraining
                                ? 'Issues detected before training - address these for better results'
                                : 'Issues detected with trained model - apply fixes to improve performance'
                            }
                        </p>
                    </div>
                    {qualityScore > 0 && (
                        <div className="ml-auto">
                            <QualityBadge score={qualityScore} />
                        </div>
                    )}
                </div>
            </div>

            {/* Failure Mode Alert */}
            {failureMode && (
                <div className="px-6 py-4 bg-amber-50 border-b border-amber-200">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div>
                            <p className="font-medium text-amber-900">
                                Model Failure Mode Detected: <span className="font-semibold">{failureMode.replace('_', ' ').toUpperCase()}</span>
                            </p>
                            <p className="text-sm text-amber-700 mt-1">
                                The system has identified specific issues and generated remediation recommendations below.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="p-6">
                {/* Issues Section */}
                {issues.length > 0 && (
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Detected Issues ({issues.length})
                        </h4>
                        <div className="space-y-3">
                            {issues.map((issue, idx) => (
                                <div
                                    key={idx}
                                    className={`rounded-lg border p-4 ${getSeverityBg(issue.severity)}`}
                                >
                                    <div className="flex items-start gap-3">
                                        {getSeverityIcon(issue.severity)}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-slate-900">{issue.description}</p>

                                            {/* Guidance */}
                                            {issue.guidance && issue.guidance.length > 0 && (
                                                <div className="mt-2">
                                                    <p className="text-xs font-semibold text-slate-700 mb-1">Guidance:</p>
                                                    <ul className="text-sm text-slate-700 space-y-1">
                                                        {issue.guidance.map((guide, gIdx) => (
                                                            <li key={gIdx} className="flex items-start gap-2">
                                                                <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-500" />
                                                                <span>{guide}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* Affected Channels */}
                                            {issue.affected_channels && issue.affected_channels.length > 0 && (
                                                <div className="mt-2">
                                                    <p className="text-xs text-slate-600">
                                                        Affected: {issue.affected_channels.slice(0, 3).join(', ')}
                                                        {issue.affected_channels.length > 3 && ` +${issue.affected_channels.length - 3} more`}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Quick Fix Button */}
                                            {issue.auto_fixable && onApplyFix && (
                                                <button
                                                    onClick={() => {
                                                        const fixAction = getFixActionFromIssue(issue);
                                                        if (fixAction) {
                                                            onApplyFix(fixAction);
                                                        }
                                                    }}
                                                    className="mt-3 btn btn-sm bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-2"
                                                >
                                                    <Wrench className="w-4 h-4" />
                                                    Apply Quick Fix
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Recommendations Section */}
                {recommendations.length > 0 && (
                    <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Recommended Actions ({recommendations.length})
                        </h4>
                        <div className="space-y-3">
                            {recommendations.map((rec, idx) => (
                                <div
                                    key={idx}
                                    className="rounded-lg border border-slate-200 bg-white p-4 hover:border-indigo-300 transition-colors"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex flex-col gap-2">
                                            <span className={`text-xs font-semibold px-2 py-1 rounded ${getPriorityBadge(rec.priority)}`}>
                                                Priority {rec.priority}
                                            </span>
                                            <span className={`text-xs font-medium px-2 py-1 rounded ${getImpactBadge(rec.expected_impact)}`}>
                                                {rec.expected_impact.toUpperCase()} impact
                                            </span>
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-slate-900">{rec.description}</p>
                                            <p className="text-sm text-slate-600 mt-1">{rec.rationale}</p>
                                            <div className="mt-2">
                                                <span className="inline-block text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded">
                                                    {rec.category}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

interface QualityBadgeProps {
    score: number;
}

export const QualityBadge: React.FC<QualityBadgeProps> = ({ score }) => {
    const getColor = () => {
        if (score >= 0.8) return { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle };
        if (score >= 0.6) return { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: AlertTriangle };
        return { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle };
    };

    const { bg, text, icon: Icon } = getColor();
    const percentage = Math.round(score * 100);

    return (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${bg}`}>
            <Icon className={`w-4 h-4 ${text}`} />
            <span className={`text-sm font-semibold ${text}`}>
                Quality: {percentage}%
            </span>
        </div>
    );
};
